import { useState, useEffect, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { getBadges } from '../api/badges';
import { issueManualBulk, BulkIssueResult, BulkIssueStatus } from '../api/badge-access';
import { Badge } from '@tinyrewards/shared';

interface BulkIssueDialogProps {
    open: boolean;
    onClose: () => void;
    onIssued?: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Split a free-form blob of emails on newline, comma, semicolon or whitespace,
 * trim, drop blanks, keep only address-shaped tokens, and de-dupe case-insensitively
 * (preserving the first-seen casing).
 */
function parseEmails(raw: string): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const token of raw.split(/[\s,;]+/)) {
        const email = token.trim();
        if (!email || !EMAIL_RE.test(email)) continue;
        const key = email.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(email);
    }
    return out;
}

function statusColor(status: BulkIssueStatus): 'success' | 'info' | 'error' {
    switch (status) {
        case 'issued': return 'success';
        case 'duplicate': return 'info';
        case 'error': return 'error';
    }
}

export default function BulkIssueDialog({ open, onClose, onIssued }: BulkIssueDialogProps) {
    const [badges, setBadges] = useState<Badge[]>([]);
    const [loadingBadges, setLoadingBadges] = useState(false);
    const [badgesError, setBadgesError] = useState<string | null>(null);

    const [badge, setBadge] = useState<Badge | null>(null);
    const [emailText, setEmailText] = useState('');
    const [skipExisting, setSkipExisting] = useState(true);
    const [notify, setNotify] = useState(true);

    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<BulkIssueResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const emails = useMemo(() => parseEmails(emailText), [emailText]);

    const loadBadges = useCallback(() => {
        setLoadingBadges(true);
        setBadgesError(null);
        getBadges({ pageSize: 500, activeFilter: 'active' })
            .then(res => {
                if (res.success) {
                    setBadges(res.data ?? []);
                } else {
                    setBadgesError(res.message ?? 'Failed to load badges.');
                }
            })
            .catch(() => setBadgesError('Failed to load badges.'))
            .finally(() => setLoadingBadges(false));
    }, []);

    useEffect(() => {
        if (open) {
            loadBadges();
            setBadge(null);
            setEmailText('');
            setSkipExisting(true);
            setNotify(true);
            setSubmitting(false);
            setResult(null);
            setError(null);
        }
    }, [open, loadBadges]);

    const handleSubmit = async () => {
        if (!badge || emails.length === 0) return;
        setSubmitting(true);
        setError(null);
        const res = await issueManualBulk(badge.badgeID, emails, { skipExisting, notify });
        setSubmitting(false);
        if (res.success && res.data) {
            setResult(res.data);
            onIssued?.();
        } else {
            setError(res.message ?? 'Failed to issue badges.');
        }
    };

    const resetForm = () => {
        setBadge(null);
        setEmailText('');
        setSkipExisting(true);
        setNotify(true);
        setResult(null);
        setError(null);
    };

    const canSubmit = !!badge && emails.length > 0 && !submitting;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Bulk issue badges</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {badgesError && <Alert severity="error" sx={{ mb: 2 }}>{badgesError}</Alert>}

                {result ? (
                    <>
                        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                            <Chip label={`${result.issued} issued`} color="success" />
                            <Chip label={`${result.duplicates} duplicate${result.duplicates !== 1 ? 's' : ''}`} color="info" />
                            <Chip label={`${result.errors} error${result.errors !== 1 ? 's' : ''}`} color="error" />
                        </Box>
                        <Divider sx={{ mb: 1 }} />
                        <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
                            {result.results.map((row, i) => (
                                <ListItem
                                    key={`${row.email}-${i}`}
                                    secondaryAction={
                                        <Chip label={row.status} size="small" color={statusColor(row.status)} />
                                    }
                                >
                                    <ListItemText
                                        primary={row.email}
                                        secondary={row.message}
                                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                        secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Autocomplete
                            size="small"
                            fullWidth
                            loading={loadingBadges}
                            options={badges}
                            getOptionLabel={o => o.badgeName}
                            isOptionEqualToValue={(a, b) => a.badgeID === b.badgeID}
                            value={badge}
                            onChange={(_, v) => setBadge(v)}
                            renderOption={(props, option) => (
                                <li {...props} key={option.badgeID}>
                                    <Box>
                                        <Typography variant="body2">{option.badgeName}</Typography>
                                        {option.issuerName && (
                                            <Typography variant="caption" color="text.secondary">
                                                {option.issuerName}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Badge to issue"
                                    placeholder="Search badges…"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingBadges ? <CircularProgress size={16} color="inherit" /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <TextField
                            label="Recipient emails"
                            placeholder="Paste emails separated by commas, semicolons, spaces, or new lines"
                            value={emailText}
                            onChange={e => setEmailText(e.target.value)}
                            multiline
                            minRows={5}
                            fullWidth
                            size="small"
                            helperText={`${emails.length} recipient${emails.length !== 1 ? 's' : ''} parsed`}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={skipExisting}
                                    onChange={e => setSkipExisting(e.target.checked)}
                                />
                            }
                            label="Skip recipients who already hold this badge"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={notify}
                                    onChange={e => setNotify(e.target.checked)}
                                />
                            }
                            label="Email recipients that they've been awarded the badge"
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {result ? (
                    <>
                        <Button color="secondary" onClick={resetForm}>Issue another batch</Button>
                        <Button variant="contained" onClick={onClose}>Done</Button>
                    </>
                ) : (
                    <>
                        <Button color="secondary" onClick={onClose}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
                            {submitting
                                ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                : null}
                            {submitting ? 'Issuing…' : `Issue${emails.length ? ` (${emails.length})` : ''}`}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
